import { h } from "preact";
import { render } from "preact-render-to-string";
import { differenceInCalendarMonths, intervalToDuration } from "date-fns";
import escape from "lodash/escape";
import snakeCase from "lodash/snakeCase";

import cleaveConfig from "./cleaveConfig.json";
import Icon from "./components/Icon.jsx";
import RedactedInfo from "./components/RedactedInfo.jsx";
import i18n from "./i18n";

let nextId = 1;

export function removeTrailingSlash(str) {
  str = str.trim();
  if (str === "/") return str;
  return str.replace(/\/$/, "");
}

export function countryCodeToFlagUrl(code) {
  return `/images/flags/${(code || "").toLowerCase()}.svg`;
}
export function stripKeys(obj, keys) {
  return Object.keys(obj)
    .filter((key) => !keys.includes(key))
    .reduce((memo, key) => ({ ...memo, [key]: obj[key] }), {});
}

export function stringTofileName(str) {
  return encodeURIComponent(str.toLowerCase().trim()).replaceAll("%20", "_");
}

export function uniqueID() {
  return nextId++;
}

export function textContains(text, str) {
  return text.toLowerCase().includes(str.toLowerCase());
}

export function templateReplace(str, placeholders) {
  return Object.keys(placeholders).reduce((acc, key) => {
    return acc.replaceAll(`[[${key}]]`, placeholders[key]);
  }, str);
}

export function toQueryString(params) {
  return Object.keys(params)
    .map(
      (key) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(
          typeof params[key] == "object"
            ? JSON.stringify(params[key])
            : params[key]
        )}`
    )
    .join("&");
}

export function classNames(...list) {
  return list.filter((x) => x).join(" ");
}

export function renderIcon(icon, className) {
  if (!icon) {
    return null;
  }

  if (typeof icon === "string") {
    return <Icon class={className}>{icon}</Icon>;
  } else {
    const props = icon.props || {};
    return {
      ...icon,
      props: {
        ...props,
        class: classNames(props.class, className),
      },
    };
  }
}

const LOCALE_DEFAULTS = {
  de: "de_DE",
  en: "en_US",
  hi: "hi_IN",
};

export function getCleaveConfig(locale) {
  let config = cleaveConfig[locale] || cleaveConfig[LOCALE_DEFAULTS[locale]];
  if (config) return config;

  config = cleaveConfig[`${locale}_${locale.toUpperCase()}`];
  if (config) return config;

  let fallbackLocale = Object.keys(cleaveConfig).find((x) =>
    x.startsWith(locale)
  );
  return cleaveConfig[fallbackLocale] || cleaveConfig["en_US"];
}

const MB = 1024 * 1024;
const KB = 1024;
export function humanFileSize(bytes) {
  if (bytes > MB) {
    return `${Math.floor(bytes / MB)} MB`;
  }
  if (bytes > KB) {
    return `${Math.floor(bytes / KB)} KB`;
  }
  return `${Math.floor(bytes)} bytes`;
}

export function humanReadableTimeDiff(to, from) {
  const allMonths = differenceInCalendarMonths(to, from);
  const years = Math.floor(allMonths / 12);
  const months = allMonths % 12;

  const result = [];

  if (years > 0) {
    result.push(i18n.t("date.years", { count: years }));
  }
  if (months > 0) {
    result.push(i18n.t("date.months", { count: months }));
  }

  return result.join(" ");
}

export function humanTimeSince(time) {
  const duration = intervalToDuration({
    start: time,
    end: new Date(),
  });

  if (duration.years > 0) {
    return i18n.t("date.years", { count: duration.years });
  }
  if (duration.months > 0) {
    return i18n.t("date.months", { count: duration.months });
  }
  if (duration.weeks > 0) {
    return i18n.t("date.weeks", { count: duration.weeks });
  }
  if (duration.days > 0) {
    return i18n.t("date.days", { count: duration.days });
  }
  if (duration.hours > 0) {
    return i18n.t("date.hours", { count: duration.hours });
  }
  if (duration.mins > 0) {
    return i18n.t("date.mins", { count: duration.mins });
  }
}

export function errorWithReason(message, httpError) {
  const reason = httpError?.response?.message;
  if (reason) {
    return `${message} Reason: ${reason}`;
  }
  return message;
}

export function firstCharUppercase(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function getText(answer) {
  if (Array.isArray(answer)) {
    return answer[0];
  }
  return answer;
}

export function getValue(answer) {
  if (Array.isArray(answer)) {
    return answer[1];
  }
  return answer;
}

export function taskState(task) {
  if (task.done) {
    return "done";
  }
  if (task.waiting) {
    return "waiting";
  }
  if (task.blocked) {
    return "blocked";
  }
  if (task.started) {
    return "in progress";
  }
  return "ready";
}

export function displayMoney(money, currency, digits = 2) {
  return new Intl.NumberFormat(navigator.language, {
    style: "currency",
    currency,
    maximumFractionDigits: digits,
  }).format(money);
}

export function wait(ms) {
  return new Promise((resolve) =>
    setTimeout(() => {
      resolve();
    }, ms)
  );
}

function parseStringBoolean(value) {
  if (value === "true") {
    return true;
  }
  if (value === "false") {
    return false;
  }
  return value;
}

export function getRouteBase(path) {
  return path.split("?")[0];
}

export function getRouteQueryParams(path) {
  const str = path.split("?")[1];
  const arr = str ? str.split("&") : [];

  return arr
    .map((x) => x.split("="))
    .reduce((acc, kv) => {
      if (kv[1]) {
        acc[kv[0]] = parseStringBoolean(decodeURIComponent(kv[1]));
      }
      return acc;
    }, {});
}

// From https://gist.github.com/developit/1409519fe1d62fb02a64b35a2e2fb66f
// Not needed with React
function convert(node) {
  if (node.nodeType === 3) return node.data; // Text nodes --> strings
  let props = {},
    a = node.attributes; // attributes --> props
  for (let i = 0; i < a.length; i++) props[a[i].name] = a[i].value;
  return h(node.localName, props, [].map.call(node.childNodes, convert)); // recurse children
}
export function renderHtml(html, tagName = "div", className = "") {
  const dom = new DOMParser().parseFromString(
    `<${tagName} class="${className}">${html}</${tagName}>`,
    "text/html"
  );
  return convert(dom.body.firstElementChild);
}

function nl2br(str) {
  if (typeof str === "undefined" || str === null) {
    return "";
  }
  return str.toString().replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, "$1<br />$2");
}

export function renderUserHtml(html, tagName, className) {
  const sanitizedHtml = nl2br(escape(html));
  return renderHtml(sanitizedHtml, tagName, className);
}

const DATE_REGEX = /(\d{2})\/(\d{2})\/(\d{4})/;

export function checkDateValidity(value) {
  if (!DATE_REGEX.test(value)) return false;

  let [_whole, day, month, year] = value.match(DATE_REGEX);
  const date = new Date(`${year}-${month}-${day}T00:00:00Z`);
  day = parseInt(day, 10);
  month = parseInt(month, 10);
  year = parseInt(year, 10);

  return (
    date.getFullYear() === year &&
    date.getMonth() + 1 === month &&
    date.getDate() === day
  );
}

export function addHighlightSpans(value, needle) {
  const index = value.toLowerCase().indexOf(needle.toLowerCase());
  if (needle.length === 0 || index === -1) return value;

  const needleLen = needle.length;

  return `${value.slice(0, index)}<span class="highlight">${value.slice(
    index,
    index + needleLen
  )}</span>${value.slice(index + needleLen)}`;
}

export function replaceRedactions(str, small = true) {
  return str.replaceAll(
    "xxxxxx",
    render(<RedactedInfo inline small={small} w80 />)
  );
}

export function showHostOfUrl(url) {
  const a = document.createElement("a");
  a.href = url;
  return a.hostname;
}

export function lowerCaseKeys(obj) {
  return Object.keys(obj).reduce((acc, key) => {
    acc[snakeCase(key)] = obj[key];
    return acc;
  }, {});
}

export function downloadFile(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export function talentStateIsChoosable(talent) {
  return [
    "contacted",
    "replied",
    "screened",
    "declined",
    "hired",
    "request_accepted",
  ].includes(talent.state);
}
